/**
 * sphericalHarmonicsPipeline — Real spherical harmonics visualization.
 *
 * Spherical harmonics Y_l^m are the angular part of solutions to
 * Laplace's equation in spherical coordinates. They form an
 * orthonormal basis for functions on the sphere.
 *
 * We visualize Y_l^m for various (l, m) on a sphere. The sign of the
 * function is shown via opposing colors (warm = positive, cool = negative).
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface SphericalHarmonicsPipeline {
  program: WebGLProgram | null;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  uResolution: WebGLUniformLocation | null;
  uTime: WebGLUniformLocation | null;
  uBass: WebGLUniformLocation | null;
  uMid: WebGLUniformLocation | null;
  uHigh: WebGLUniformLocation | null;
  bands: Float32Array;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass;
uniform vec3 uMid;
uniform vec3 uHigh;

float bass;
float mid;
float high;

// Real spherical harmonics Y_l^m evaluated at spherical (theta, phi).
float Y_lm(int l, int m, float theta, float phi) {
  // Use the standard real form: cos(m*phi) for m >= 0, sin(|m|*phi) for m < 0.
  float absM = float(abs(m));
  // We approximate the Legendre polynomials.
  // P_0 = 1
  // P_1 = cos(theta)
  // P_2 = (3 cos^2(theta) - 1) / 2
  // P_3 = (5 cos^3(theta) - 3 cos(theta)) / 2
  // etc.
  float c = cos(theta);
  float p;
  if (l == 0) p = 1.0;
  else if (l == 1) p = c;
  else if (l == 2) p = (3.0 * c * c - 1.0) * 0.5;
  else if (l == 3) p = (5.0 * c * c * c - 3.0 * c) * 0.5;
  else if (l == 4) p = (35.0 * c * c * c * c - 30.0 * c * c + 3.0) * 0.125;
  else p = pow(c, float(l));
  // Phi modulation.
  float phiPart;
  if (m == 0) phiPart = 1.0;
  else if (m > 0) phiPart = cos(absM * phi);
  else phiPart = sin(absM * phi);
  // Normalization factor (simplified).
  return p * phiPart;
}

void main() {
  bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  mid = (uMid.x + uMid.y + uMid.z) / 3.0;
  high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  bass = pow(clamp(bass * 2.5, 0.0, 1.0), 0.7);
  mid = pow(clamp(mid * 3.0, 0.0, 1.0), 0.7);
  high = pow(clamp(high * 4.0, 0.0, 1.0), 0.7);

  vec2 p = (gl_FragCoord.xy - uResolution * 0.5) / min(uResolution.x, uResolution.y);
  p.x *= uResolution.x / uResolution.y;
  // Sphere coords: project 2D into spherical.
  float r = length(p);
  if (r > 0.95) {
    outColor = vec4(0.0, 0.0, 0.0, 1.0);
    return;
  }
  float theta = acos(p.y / 0.95);  // polar angle
  float phi = atan(p.x, -p.y);  // azimuthal angle

  // Cycle through different (l, m) over time.
  int idx = int(mod(uTime * 0.3, 16.0));
  int l = idx / 4;
  int m = idx % 4 - 2;
  // Override m sign for variety.
  if (mod(uTime * 0.3, 8.0) > 4.0) m = -m;
  float y = Y_lm(l, m, theta, phi);

  // Color: positive = warm, negative = cool.
  vec3 posColor = vec3(1.0, 0.7, 0.2);
  vec3 negColor = vec3(0.2, 0.5, 0.95);
  vec3 col = mix(negColor, posColor, smoothstep(-0.5, 0.5, y));
  // Magnitude affects saturation.
  col *= 0.5 + abs(y) * 1.0;
  // Sphere outline.
  float outline = smoothstep(0.92, 0.95, r);
  col = mix(col, vec3(0.0), outline);
  // Background.
  vec3 bg = vec3(0.05, 0.06, 0.10);
  col = mix(bg, col, smoothstep(0.95, 0.92, r));
  // Label (small).
  vec3 labelCol = vec3(0.95, 0.85, 0.55);
  vec2 labelPos = vec2(-0.85, -0.85);
  if (length(p - labelPos) < 0.05) {
    col = mix(col, labelCol, 0.5);
  }
  // Audio: pulse.
  col *= 1.0 + high * 0.2;

  // Tone mapping.
  col = (col * (2.51 * col + 0.03)) / (col * (2.43 * col + 0.59) + 0.14);
  col = pow(clamp(col, 0.0, 1.0), vec3(1.0 / 2.2));

  outColor = vec4(col, 1.0);
}
`;

export function computeSphericalHarmonicsBands(t: number, out: Float32Array): void {
  out[0] = 0.5 + 0.3 * Math.sin(t * 0.5);
  out[1] = 0.5 + 0.3 * Math.sin(t * 0.7 + 1.0);
  out[2] = 0.4 + 0.3 * Math.sin(t * 1.0 + 2.0);
  out[3] = 0.5 + 0.3 * Math.sin(t * 1.2 + 1.5);
  out[4] = 0.5 + 0.3 * Math.sin(t * 1.4 + 0.5);
  out[5] = 0.4 + 0.3 * Math.sin(t * 1.7 + 2.5);
  out[6] = 0.3 + 0.4 * Math.sin(t * 2.1 + 0.7);
  out[7] = 0.4 + 0.3 * Math.sin(t * 2.5 + 1.8);
  out[8] = 0.3 + 0.4 * Math.sin(t * 3.0 + 3.0);
}

export function computeSphericalHarmonicsRealtimeBands(
  freqData: Uint8Array,
  out: Float32Array,
): void {
  const N = freqData.length;
  const ranges = [
    [0.00, 0.030], [0.030, 0.080], [0.080, 0.150],
    [0.150, 0.260], [0.260, 0.380], [0.380, 0.500],
    [0.500, 0.660], [0.660, 0.820], [0.820, 0.980],
  ];
  for (let i = 0; i < 9; i++) {
    const [lo, hi] = ranges[i];
    const start = Math.floor(lo * N);
    const end = Math.floor(hi * N);
    let sum = 0, count = 0;
    for (let k = start; k < end; k++) {
      sum += freqData[k];
      count++;
    }
    out[i] = count > 0 ? (sum / count) / 255 : 0;
  }
}

export function createSphericalHarmonicsPipeline(gl: WebGL2RenderingContext): SphericalHarmonicsPipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;
  const verts = new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]);
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);
  return {
    program, vao, vbo,
    uResolution: gl.getUniformLocation(program, "uResolution"),
    uTime: gl.getUniformLocation(program, "uTime"),
    uBass: gl.getUniformLocation(program, "uBass"),
    uMid: gl.getUniformLocation(program, "uMid"),
    uHigh: gl.getUniformLocation(program, "uHigh"),
    bands: new Float32Array(9),
  };
}

export function drawSphericalHarmonics(
  gl: WebGL2RenderingContext,
  pipe: SphericalHarmonicsPipeline,
  width: number,
  height: number,
  t: number,
): void {
  if (!pipe.program) return;
  gl.useProgram(pipe.program);
  const prog = pipe.program;
  gl.uniform2f(gl.getUniformLocation(prog, "uResolution"), width, height);
  gl.uniform1f(gl.getUniformLocation(prog, "uTime"), t);
  gl.uniform3f(gl.getUniformLocation(prog, "uBass"), pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  gl.uniform3f(gl.getUniformLocation(prog, "uMid"), pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  gl.uniform3f(gl.getUniformLocation(prog, "uHigh"), pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}
